import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';

/**
 * Componente de Carga Unificado
 * @param {Object} props
 * @param {'full' | 'overlay' | 'inline' | 'spinner'} props.variant - Tipo de loader
 * @param {string} [props.text] - Texto opcional a mostrar debajo del spinner
 * @param {string} [props.className] - Clases adicionales
 */
export default function Loading({ variant = 'full', text = 'Cargando...', className }) {

    // Variante: Pantalla Completa (App Load, Suspense, Auth)
    if (variant === 'full') {
        return (
            <div className={cn("min-h-screen w-full flex flex-col items-center justify-center bg-slate-50/80 backdrop-blur-sm z-50 fixed inset-0", className)}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="flex flex-col items-center"
                >
                    <div className="relative">
                        <div className="w-16 h-16 rounded-full border-4 border-slate-200 border-t-blue-600 animate-spin shadow-lg shadow-blue-500/10"></div>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">
                                <div className="w-4 h-4 rounded-full bg-blue-600 animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                    {text && (
                        <motion.p
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="mt-6 text-sm font-medium text-slate-500 tracking-wide uppercase"
                        >
                            {text}
                        </motion.p>
                    )}
                </motion.div>
            </div>
        );
    }

    // Variante: Overlay (Para bloquear contenedores específicos)
    if (variant === 'overlay') {
        return (
            <div className={cn("absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center rounded-xl transition-all duration-300", className)}>
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    {text && <p className="text-xs font-semibold text-slate-600">{text}</p>}
                </div>
            </div>
        );
    }

    // Variante: Inline (Para usar dentro de botones o textos)
    if (variant === 'inline') {
        return (
            <div className={cn("flex items-center gap-2 text-slate-500", className)}>
                <Loader2 className="h-4 w-4 animate-spin" />
                {text && <span className="text-sm">{text}</span>}
            </div>
        );
    }

    // Variante: Spinner (Solo el icono centrado, para contenedores pequeños)
    if (variant === 'spinner') {
        return (
            <div className={cn("flex items-center justify-center p-4", className)}>
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            </div>
        );
    }

    // Variante: Icon (Solo el icono, sin padding extra, para uso inline o custom)
    if (variant === 'icon') {
        return <Loader2 className={cn("h-4 w-4 animate-spin", className)} />;
    }

    return null;
}
